import React, { useState, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  Box,
  Grid,
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Alert,
  Dialog,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  Divider,
  Chip,
  Avatar,
  IconButton,
  Stack,
} from '@mui/material';
import UserService from '../pages/services/UserService';
import { fNumber } from '../utils/formatNumber';

export const getActiveViewAs = () => {
  return {
    role: sessionStorage.getItem('mtc_view_as_role') || '',
    userId: sessionStorage.getItem('mtc_view_as_user') || '',
    userName: sessionStorage.getItem('mtc_view_as_name') || '',
  };
};

export const clearViewAs = () => {
  sessionStorage.removeItem('mtc_view_as_role');
  sessionStorage.removeItem('mtc_view_as_user');
  sessionStorage.removeItem('mtc_view_as_name');
  sessionStorage.removeItem('mtc_user_selected_personal_tab');
  window.dispatchEvent(new Event('mtc-view-as-change'));
  window.location.reload();
};

export const setViewAs = (role, userId = '', userName = '') => {
  if (!role || role === 'admin') {
    clearViewAs();
    return;
  }
  sessionStorage.setItem('mtc_view_as_role', role);
  if (userId) sessionStorage.setItem('mtc_view_as_user', userId);
  else sessionStorage.removeItem('mtc_view_as_user');
  if (userName) sessionStorage.setItem('mtc_view_as_name', userName);
  else sessionStorage.removeItem('mtc_view_as_name');
  sessionStorage.removeItem('mtc_user_selected_personal_tab');

  window.dispatchEvent(new Event('mtc-view-as-change'));
  window.location.reload();
};

// Simulation Banner to display at the top of DashboardLayout when View As mode is active
export const SimulationBanner = () => {
  const [active, setActive] = useState(() => getActiveViewAs());

  useEffect(() => {
    const handleUpdate = () => setActive(getActiveViewAs());
    window.addEventListener('mtc-view-as-change', handleUpdate);
    return () => window.removeEventListener('mtc-view-as-change', handleUpdate);
  }, []);

  if (!active.role) return null;

  const roleLabels = {
    financial: 'Financial I',
    financial_1: 'Financial I',
    financial_2: 'Financial II',
    financial_3: 'Financial III',
    secretary: 'Secretary',
    registrar: 'Secretary (Registrar)',
    president: 'President',
    business: 'Business Officer',
    user: 'Regular Member (User)',
  };

  const roleDisplay = roleLabels[active.role] || active.role;
  const userDisplay = active.userName ? ` (${active.userName})` : '';

  return (
    <Box sx={{ mb: 2.5 }}>
      <Alert
        severity="warning"
        variant="filled"
        sx={{
          borderRadius: 2,
          boxShadow: '0 4px 12px rgba(245, 158, 11, 0.25)',
          alignItems: 'center',
          backgroundColor: '#d97706',
          color: '#ffffff',
          fontWeight: 500,
          flexWrap: { xs: 'wrap', sm: 'nowrap' },
          '& .MuiAlert-action': {
            ml: { xs: 0, sm: 'auto' },
            mt: { xs: 1, sm: 0 },
            pt: 0,
            width: { xs: '100%', sm: 'auto' },
            justifyContent: { xs: 'flex-start', sm: 'flex-end' },
          },
        }}
        action={
          <Button
            size="small"
            variant="contained"
            onClick={clearViewAs}
            sx={{
              backgroundColor: '#ffffff',
              color: '#92400e',
              fontWeight: 700,
              '&:hover': { backgroundColor: '#fef3c7' },
              textTransform: 'none',
              px: 2,
            }}
          >
            Exit View As Mode
          </Button>
        }
      >
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          Simulation Mode Active: You are viewing the application as <u>{roleDisplay}{userDisplay}</u> for testing and training.
        </Typography>
      </Alert>
    </Box>
  );
};

// Dropdown Selector in Header for Overall Admin
const getStatusChipProps = (status) => {
  const s = String(status || 'Active').toLowerCase();
  if (s === 'non-active' || s === 'inactive' || s === 'non active') {
    return { bgcolor: '#FEF3C7', color: '#B45309', label: 'Non-Active' };
  }
  if (s === 'withdraw' || s === 'withdrawn' || s === 'withdrawal') {
    return { bgcolor: '#FEF9C3', color: '#854D0E', label: 'Withdraw' };
  }
  if (s === 'expel' || s === 'expelled') {
    return { bgcolor: '#FEE2E2', color: '#B91C1C', label: 'Expel' };
  }
  if (s === 'decease' || s === 'deceased') {
    return { bgcolor: '#F1F5F9', color: '#475569', label: 'Decease' };
  }
  return { bgcolor: '#ECFDF5', color: '#047857', label: 'Active' };
};

export default function ViewAsRoleSelector() {
  const [anchorEl, setAnchorEl] = useState(null);
  const [officers, setOfficers] = useState([]);
  const [allMembers, setAllMembers] = useState([]);
  const [active, setActive] = useState(() => getActiveViewAs());
  const [officerModalOpen, setOfficerModalOpen] = useState(false);
  const [pendingRole, setPendingRole] = useState('');
  const [selectedOfficerId, setSelectedOfficerId] = useState('');

  const reduxUser = useSelector((state) => state?.auth?.user);

  // Check if current user is actual Admin
  const isActualAdmin = useMemo(() => {
    try {
      const stored = localStorage.getItem('user') || localStorage.getItem('profile');
      const u = reduxUser || (stored ? JSON.parse(stored) : {});
      if (!u || Object.keys(u).length === 0) return false;
      if (Boolean(u.isAdmin)) return true;

      const parts = [];
      if (Array.isArray(u.roles)) parts.push(...u.roles);
      else if (u.roles) parts.push(u.roles);

      if (Array.isArray(u.authorities)) parts.push(...u.authorities);
      else if (u.authorities) parts.push(u.authorities);

      if (Array.isArray(u.role)) parts.push(...u.role);
      else if (u.role) parts.push(u.role);

      if (u.roleName) parts.push(u.roleName);
      if (u.name) parts.push(u.name);

      const hasAdminRole = parts.some((r) => {
        const str = (typeof r === 'object' && r !== null ? (r.name || r.authority || String(r)) : String(r)).toLowerCase();
        return str.includes('admin');
      });

      if (hasAdminRole) return true;

      // Fallback for default admin usernames
      const uname = String(u.username || u.email || '').toLowerCase();
      if (uname === 'admin' || uname.startsWith('admin@')) return true;

      return false;
    } catch {
      return false;
    }
  }, [reduxUser]);

  useEffect(() => {
    if (isActualAdmin) {
      UserService.getFinancialOfficers()
        .then((res) => setOfficers(res.data || []))
        .catch(() => {});
      UserService.adminGetAll()
        .then((res) => setAllMembers(res.data || []))
        .catch(() => {});
    }
  }, [isActualAdmin]);

  useEffect(() => {
    const handleUpdate = () => setActive(getActiveViewAs());
    window.addEventListener('mtc-view-as-change', handleUpdate);
    return () => window.removeEventListener('mtc-view-as-change', handleUpdate);
  }, []);

  if (!isActualAdmin) return null;

  const handleOpenMenu = (event) => setAnchorEl(event.currentTarget);
  const handleCloseMenu = () => setAnchorEl(null);

  const getOfficersForRole = (role) => {
    if (role === 'user') return allMembers;
    return officers.filter((o) => {
      const parts = [];
      if (Array.isArray(o.roles)) parts.push(...o.roles.map((r) => (typeof r === 'object' && r !== null ? r.name : String(r))));
      else if (o.roles) parts.push(String(o.roles));
      if (Array.isArray(o.role)) parts.push(...o.role);
      else if (o.role) parts.push(String(o.role));
      if (o.roleName) parts.push(String(o.roleName));
      const str = parts.join(' ').toLowerCase();

      if (role === 'financial' || role === 'financial_1') return str.includes('financial_1') || (str.includes('financial') && !str.includes('financial_2') && !str.includes('financial_3'));
      if (role === 'financial_2') return str.includes('financial_2');
      if (role === 'financial_3') return str.includes('financial_3');
      if (role === 'business') return str.includes('business');
    if (role === 'president') return str.includes('president');
      if (role === 'secretary' || role === 'registrar') return str.includes('registrar') || str.includes('secretary');
      return false;
    });
  };

  const handleSelectRole = (role) => {
    handleCloseMenu();
    if (role === 'admin') {
      clearViewAs();
      return;
    }

    setPendingRole(role);
    if (role === 'user') {
      setSelectedOfficerId('');
      setOfficerModalOpen(true);
      return;
    }
    const matchingOfficers = getOfficersForRole(role);
    if (matchingOfficers.length > 0) {
      setSelectedOfficerId(matchingOfficers[0]._id || matchingOfficers[0].id);
    } else {
      setSelectedOfficerId('');
    }
    setOfficerModalOpen(true);
  };

  const handleConfirmOfficer = () => {
    setOfficerModalOpen(false);
    if (selectedOfficerId) {
      const pool = pendingRole === 'user' ? allMembers : officers;
      const chosen = pool.find((o) => (o._id || o.id) === selectedOfficerId);
      const name = chosen ? (chosen.firstName ? `${chosen.firstName} ${chosen.lastName || ''}`.trim() : chosen.username) : '';
      setViewAs(pendingRole, selectedOfficerId, name);
    } else {
      setViewAs(pendingRole);
    }
  };

  const roleLabels = {
    financial: 'Financial I',
    financial_2: 'Financial II',
    financial_3: 'Financial III',
    secretary: 'Secretary',
    president: 'President',
    business: 'Business Officer',
    user: 'Regular Member (User)',
  };

  const activeLabel = active.role ? (roleLabels[active.role] || active.role) : 'Overall Admin';

  const matchingOfficers = pendingRole === 'user' ? allMembers : getOfficersForRole(pendingRole);
  const selectedOfficer = (pendingRole === 'user' ? allMembers : officers).find((x) => (x._id || x.id) === selectedOfficerId) || (pendingRole === 'user' ? null : matchingOfficers[0]) || null;

  return (
    <>
      <Button
        variant={active.role ? 'contained' : 'outlined'}
        color={active.role ? 'warning' : 'inherit'}
        size="small"
        onClick={handleOpenMenu}
        sx={{
          textTransform: 'none',
          borderRadius: 2,
          fontWeight: 700,
          px: { xs: 1, sm: 1.5 },
          py: 0.6,
          fontSize: { xs: '0.78rem', sm: '0.82rem' },
          display: 'flex',
          alignItems: 'center',
          gap: 0.8,
          backgroundColor: active.role ? '#d97706' : 'rgba(0, 0, 0, 0.04)',
          color: active.role ? '#ffffff' : '#0B3B60',
          border: active.role ? 'none' : '1px solid #CBD5E1',
          boxShadow: active.role ? '0 2px 8px rgba(217, 119, 6, 0.3)' : 'none',
          '&:hover': {
            backgroundColor: active.role ? '#b45309' : 'rgba(0, 0, 0, 0.08)',
          },
        }}
      >
        <span style={{ fontSize: '1rem' }}>👁️</span>
        <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
          View As:&nbsp;
        </Box>
        <strong>{activeLabel}</strong>
        <span style={{ fontSize: '0.75rem', opacity: 0.8 }}>▾</span>
      </Button>

      {/* Role Selector Dropdown Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        PaperProps={{
          sx: {
            mt: 1,
            width: { xs: 'calc(100vw - 32px)', sm: 360 },
            maxWidth: 380,
            borderRadius: 2.5,
            boxShadow: '0 12px 32px rgba(11, 59, 96, 0.16)',
            border: '1px solid #E2E8F0',
            maxHeight: '85vh',
            overflowY: 'auto',
          },
        }}
      >
        <Box sx={{ px: 2, py: 1.2, backgroundColor: '#F8FAFC', borderBottom: '1px solid #E2E8F0' }}>
          <Typography variant="caption" sx={{ color: '#0B3B60', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Role Testing & Simulation
          </Typography>
          <Typography variant="caption" sx={{ color: '#64748B', display: 'block', fontSize: '0.75rem' }}>
            Select any executive role to preview pages and test permissions:
          </Typography>
        </Box>

        {/* Overall Admin */}
        <MenuItem
          onClick={() => handleSelectRole('admin')}
          selected={!active.role}
          sx={{ py: 1.2, px: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mr: 1, flex: 1 }}>
            <ListItemIcon sx={{ minWidth: 28, mt: 0.2, fontSize: '1.25rem' }}>🛡️</ListItemIcon>
            <ListItemText
              primary="Overall Admin"
              secondary="Full system administration & settings"
              primaryTypographyProps={{ fontWeight: 700, fontSize: '0.875rem', color: '#0F172A' }}
              secondaryTypographyProps={{ fontSize: '0.75rem', color: '#64748B', whiteSpace: 'normal' }}
            />
          </Box>
          <Chip size="small" label="Normal Mode" sx={{ height: 20, fontSize: '0.68rem', fontWeight: 700, bgcolor: '#ECFDF5', color: '#047857' }} />
        </MenuItem>

        <Divider sx={{ my: 0.5 }} />

        {/* Financial I */}
        <MenuItem
          onClick={() => handleSelectRole('financial')}
          selected={active.role === 'financial' || active.role === 'financial_1'}
          sx={{ py: 1.2, px: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mr: 1, flex: 1 }}>
            <ListItemIcon sx={{ minWidth: 28, mt: 0.2, fontSize: '1.25rem' }}>💼</ListItemIcon>
            <ListItemText
              primary="Financial I"
              secondary="Full financial portfolio & reconciliation manager"
              primaryTypographyProps={{ fontWeight: 700, fontSize: '0.875rem', color: '#0F172A' }}
              secondaryTypographyProps={{ fontSize: '0.75rem', color: '#64748B', whiteSpace: 'normal' }}
            />
          </Box>
          <Chip
            size="small"
            label={`${getOfficersForRole('financial').length} Officer${getOfficersForRole('financial').length === 1 ? '' : 's'}`}
            sx={{ height: 20, fontSize: '0.68rem', fontWeight: 700, bgcolor: '#F1F5F9', color: '#334155' }}
          />
        </MenuItem>

        {/* Financial II */}
        <MenuItem
          onClick={() => handleSelectRole('financial_2')}
          selected={active.role === 'financial_2'}
          sx={{ py: 1.2, px: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mr: 1, flex: 1 }}>
            <ListItemIcon sx={{ minWidth: 28, mt: 0.2, fontSize: '1.25rem' }}>📊</ListItemIcon>
            <ListItemText
              primary="Financial II"
              secondary="Allocated member portfolio supervision only"
              primaryTypographyProps={{ fontWeight: 700, fontSize: '0.875rem', color: '#0F172A' }}
              secondaryTypographyProps={{ fontSize: '0.75rem', color: '#64748B', whiteSpace: 'normal' }}
            />
          </Box>
          <Chip
            size="small"
            label={`${getOfficersForRole('financial_2').length} Officer${getOfficersForRole('financial_2').length === 1 ? '' : 's'}`}
            sx={{ height: 20, fontSize: '0.68rem', fontWeight: 700, bgcolor: '#F1F5F9', color: '#334155' }}
          />
        </MenuItem>

                {/* Business Officer */}
        <MenuItem
          onClick={() => handleSelectRole('business')}
          selected={active.role === 'business'}
          sx={{ py: 1.2, px: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mr: 1, flex: 1 }}>
            <ListItemIcon sx={{ minWidth: 28, mt: 0.2, fontSize: '1.25rem' }}>🏪</ListItemIcon>
            <ListItemText
              primary="Business Officer"
              secondary="Manage transactions on Business account only"
              primaryTypographyProps={{ fontWeight: 700, fontSize: '0.875rem', color: '#0F172A' }}
              secondaryTypographyProps={{ fontSize: '0.75rem', color: '#64748B', whiteSpace: 'normal' }}
            />
          </Box>
          <Chip
            size="small"
            label={`${getOfficersForRole('business').length} Officer${getOfficersForRole('business').length === 1 ? '' : 's'}`}
            sx={{ height: 20, fontSize: '0.68rem', fontWeight: 700, bgcolor: '#F1F5F9', color: '#334155' }}
          />
        </MenuItem>

        {/* Financial III */}
        <MenuItem
          onClick={() => handleSelectRole('financial_3')}
          selected={active.role === 'financial_3'}
          sx={{ py: 1.2, px: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mr: 1, flex: 1 }}>
            <ListItemIcon sx={{ minWidth: 28, mt: 0.2, fontSize: '1.25rem' }}>📈</ListItemIcon>
            <ListItemText
              primary="Financial III"
              secondary="Allocated member portfolio supervision only"
              primaryTypographyProps={{ fontWeight: 700, fontSize: '0.875rem', color: '#0F172A' }}
              secondaryTypographyProps={{ fontSize: '0.75rem', color: '#64748B', whiteSpace: 'normal' }}
            />
          </Box>
          <Chip
            size="small"
            label={`${getOfficersForRole('financial_3').length} Officer${getOfficersForRole('financial_3').length === 1 ? '' : 's'}`}
            sx={{ height: 20, fontSize: '0.68rem', fontWeight: 700, bgcolor: '#F1F5F9', color: '#334155' }}
          />
        </MenuItem>

        <Divider sx={{ my: 0.5 }} />

        {/* Secretary */}
        <MenuItem
          onClick={() => handleSelectRole('secretary')}
          selected={active.role === 'secretary' || active.role === 'registrar'}
          sx={{ py: 1.2, px: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mr: 1, flex: 1 }}>
            <ListItemIcon sx={{ minWidth: 28, mt: 0.2, fontSize: '1.25rem' }}>📝</ListItemIcon>
            <ListItemText
              primary="Secretary / Registrar"
              secondary="Member registration & profile management"
              primaryTypographyProps={{ fontWeight: 700, fontSize: '0.875rem', color: '#0F172A' }}
              secondaryTypographyProps={{ fontSize: '0.75rem', color: '#64748B', whiteSpace: 'normal' }}
            />
          </Box>
          <Chip
            size="small"
            label={`${getOfficersForRole('secretary').length} Officer${getOfficersForRole('secretary').length === 1 ? '' : 's'}`}
            sx={{ height: 20, fontSize: '0.68rem', fontWeight: 700, bgcolor: '#F1F5F9', color: '#334155' }}
          />
        </MenuItem>

        {/* President */}
        <MenuItem
          onClick={() => handleSelectRole('president')}
          selected={active.role === 'president'}
          sx={{ py: 1.2, px: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mr: 1, flex: 1 }}>
            <ListItemIcon sx={{ minWidth: 28, mt: 0.2, fontSize: '1.25rem' }}>🏛️</ListItemIcon>
            <ListItemText
              primary="President"
              secondary="Executive summary statement & full ledger overview"
              primaryTypographyProps={{ fontWeight: 700, fontSize: '0.875rem', color: '#0F172A' }}
              secondaryTypographyProps={{ fontSize: '0.75rem', color: '#64748B', whiteSpace: 'normal' }}
            />
          </Box>
          <Chip
            size="small"
            label={`${getOfficersForRole('president').length} Officer${getOfficersForRole('president').length === 1 ? '' : 's'}`}
            sx={{ height: 20, fontSize: '0.68rem', fontWeight: 700, bgcolor: '#F1F5F9', color: '#334155' }}
          />
        </MenuItem>
        <Divider sx={{ my: 0.5 }} />

        {/* Regular Member (User) */}
        <MenuItem
          onClick={() => handleSelectRole('user')}
          selected={active.role === 'user'}
          sx={{ py: 1.2, px: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mr: 1, flex: 1 }}>
            <ListItemIcon sx={{ minWidth: 28, mt: 0.2, fontSize: '1.25rem' }}>👤</ListItemIcon>
            <ListItemText
              primary="Regular Member (User)"
              secondary="Test member personal accounts (savings, loan, shares, business, etc.)"
              primaryTypographyProps={{ fontWeight: 700, fontSize: '0.875rem', color: '#0F172A' }}
              secondaryTypographyProps={{ fontSize: '0.75rem', color: '#64748B', whiteSpace: 'normal' }}
            />
          </Box>
          <Chip
            size="small"
            label="Member Access"
            sx={{ height: 20, fontSize: '0.68rem', fontWeight: 700, bgcolor: '#EFF6FF', color: '#1D4ED8' }}
          />
        </MenuItem>
      </Menu>

      {/* Comprehensive Officer Details & Confirmation Dialog */}
      <Dialog
        open={officerModalOpen}
        onClose={() => setOfficerModalOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: 'hidden',
            boxShadow: '0 24px 48px -12px rgba(11, 59, 96, 0.25)',
            border: '1px solid #E2E8F0',
            m: { xs: 1.5, sm: 2 },
          },
        }}
      >
        <Box
          sx={{
            background: 'linear-gradient(135deg, #0B3B60 0%, #072740 100%)',
            color: '#FFFFFF',
            p: { xs: 2.5, sm: 3 },
            position: 'relative',
          }}
        >
          <IconButton
            onClick={() => setOfficerModalOpen(false)}
            sx={{
              position: 'absolute',
              right: 12,
              top: 12,
              color: 'rgba(255, 255, 255, 0.8)',
              '&:hover': { color: '#FFFFFF', backgroundColor: 'rgba(255, 255, 255, 0.1)' },
            }}
          >
            ✕
          </IconButton>

          <Typography variant="overline" sx={{ color: 'rgba(255, 255, 255, 0.75)', fontWeight: 700, letterSpacing: '0.08em' }}>
            Officer Testing & Profile Preview
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 800, mt: 0.5, color: '#FFFFFF' }}>
            Test As {roleLabels[pendingRole] || 'Officer'}
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.85)', mt: 0.5 }}>
            Preview this officer's dashboard, assigned member allocations, and individual permissions:
          </Typography>
        </Box>

                <DialogContent sx={{ p: { xs: 2, sm: 3 }, backgroundColor: '#F8FAFC' }}>
          {pendingRole === 'user' ? (
            <Stack spacing={2}>
              <FormControl fullWidth size="small">
                <InputLabel id="member-picker-label" sx={{ fontWeight: 600 }}>Select Member Account to Simulate</InputLabel>
                <Select
                  labelId="member-picker-label"
                  label="Select Member Account to Simulate"
                  value={selectedOfficerId || ''}
                  onChange={(e) => setSelectedOfficerId(e.target.value)}
                  sx={{ backgroundColor: '#FFFFFF', borderRadius: 2 }}
                >
                  <MenuItem value="">
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#0F172A' }}>
                        Current Account (Standard Member Mode)
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#64748B' }}>
                        Simulate standard member permissions using current logged-in user
                      </Typography>
                    </Box>
                  </MenuItem>
                  {allMembers.map((m) => {
                    const name = m.firstName ? `${m.firstName} ${m.lastName || ''}`.trim() : m.username;
                    const idInfo = m.membershipId ? `ID: ${m.membershipId}` : (m.registrationNo ? `Reg: ${m.registrationNo}` : '');
                    return (
                      <MenuItem key={m._id || m.id} value={m._id || m.id}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#0F172A' }}>{name}</Typography>
                            {idInfo && <Typography variant="caption" sx={{ color: '#64748B' }}>{idInfo}</Typography>}
                          </Box>
                          <Chip
                            size="small"
                            label={getStatusChipProps(m.approvedOrNotApproved).label}
                            sx={{
                              height: 20,
                              fontSize: '0.68rem',
                              fontWeight: 700,
                              bgcolor: getStatusChipProps(m.approvedOrNotApproved).bgcolor,
                              color: getStatusChipProps(m.approvedOrNotApproved).color,
                            }}
                          />
                        </Box>
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>

              <Box sx={{ p: 2, backgroundColor: 'rgba(37, 99, 235, 0.08)', borderRadius: 2, border: '1px solid rgba(37, 99, 235, 0.25)' }}>
                <Typography variant="caption" sx={{ color: '#1D4ED8', fontWeight: 800, textTransform: 'uppercase', display: 'block', letterSpacing: '0.04em' }}>
                  Member Access & Permission Scope
                </Typography>
                <Typography variant="body2" sx={{ color: '#1E40AF', fontWeight: 700, mt: 0.3 }}>
                  Personal Member Accounts Only
                </Typography>
                <Typography variant="caption" sx={{ color: '#1D4ED8', display: 'block', fontSize: '0.75rem', mt: 0.5, lineHeight: 1.5 }}>
                  • Standard members can view only their personal Savings, Loan, Shares, Ileya Savings, Business, etc.<br/>
                  • Financial officer management, member lists, summary account, and reconciliation are strictly restricted.<br/>
                  • Selecting a specific member allows testing their exact balances and transactions.
                </Typography>
              </Box>

              {selectedOfficer && (
                <Box
                  sx={{
                    backgroundColor: '#FFFFFF',
                    borderRadius: 2.5,
                    border: '1px solid #E2E8F0',
                    p: { xs: 2, sm: 2.5 },
                    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.8, mb: 2, pb: 2, borderBottom: '1px solid #F1F5F9' }}>
                    <Avatar
                      sx={{
                        width: 48,
                        height: 48,
                        bgcolor: '#1D4ED8',
                        color: '#FFFFFF',
                        fontWeight: 700,
                        fontSize: '1.2rem',
                      }}
                    >
                      {(selectedOfficer.firstName || selectedOfficer.username || 'M').charAt(0).toUpperCase()}
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0F172A', lineHeight: 1.2 }}>
                          {selectedOfficer.firstName
                            ? `${selectedOfficer.firstName} ${selectedOfficer.middleName ? selectedOfficer.middleName + ' ' : ''}${selectedOfficer.lastName || ''}`.trim()
                            : selectedOfficer.username}
                        </Typography>
                        <Chip
                          size="small"
                          label={getStatusChipProps(selectedOfficer.approvedOrNotApproved).label}
                          sx={{
                            height: 20,
                            fontSize: '0.68rem',
                            fontWeight: 700,
                            bgcolor: getStatusChipProps(selectedOfficer.approvedOrNotApproved).bgcolor,
                            color: getStatusChipProps(selectedOfficer.approvedOrNotApproved).color,
                          }}
                        />
                      </Box>
                      <Typography variant="caption" sx={{ color: '#64748B', display: 'block', mt: 0.3 }}>
                        @{selectedOfficer.username} {selectedOfficer.email ? `• ${selectedOfficer.email}` : ''}
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ p: 1.5, backgroundColor: '#F8FAFC', borderRadius: 2, border: '1px solid #E2E8F0' }}>
                    <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 800, textTransform: 'uppercase', display: 'block', letterSpacing: '0.04em', mb: 0.5 }}>
                      Personal Account Balances
                    </Typography>
                    <Grid container spacing={1}>
                      <Grid item xs={3}>
                        <Typography variant="caption" sx={{ color: '#64748B', display: 'block' }}>Shares</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#0B3B60' }}>₦{fNumber(selectedOfficer.sharesBalance || 0)}</Typography>
                      </Grid>
                      <Grid item xs={3}>
                        <Typography variant="caption" sx={{ color: '#64748B', display: 'block' }}>Savings</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#059669' }}>₦{fNumber(selectedOfficer.savingsBalance || 0)}</Typography>
                      </Grid>
                      <Grid item xs={3}>
                        <Typography variant="caption" sx={{ color: '#64748B', display: 'block' }}>Loan</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#D97706' }}>₦{fNumber(selectedOfficer.loanBalance || 0)}</Typography>
                      </Grid>
                      <Grid item xs={3}>
                        <Typography variant="caption" sx={{ color: '#64748B', display: 'block' }}>Business</Typography>
                        <Typography variant="body2" sx={{ fontWeight: 700, color: '#0D9488' }}>₦{fNumber(selectedOfficer.businessBalance || 0)}</Typography>
                      </Grid>
                    </Grid>
                  </Box>
                </Box>
              )}
            </Stack>
          ) : matchingOfficers.length === 0 ? (
            <Alert severity="info" sx={{ borderRadius: 2, mb: 1, '& .MuiAlert-message': { width: '100%' } }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#0B3B60', mb: 0.5 }}>
                No Registered Officer for {roleLabels[pendingRole] || pendingRole} Yet
              </Typography>
              <Typography variant="body2" sx={{ color: '#334155', fontSize: '0.85rem', lineHeight: 1.5 }}>
                There are currently no member accounts in the database holding the <strong>{roleLabels[pendingRole] || pendingRole}</strong> role.
              </Typography>
              <Typography variant="body2" sx={{ color: '#64748B', fontSize: '0.8rem', mt: 1 }}>
                You can still proceed with <strong>Role Simulation</strong> mode to test the permissions, views, and navigation, or assign an officer in the Users management page.
              </Typography>
            </Alert>
          ) : (
            <Stack spacing={2}>
              {/* Dropdown if multiple officers exist for this role */}
              {matchingOfficers.length > 1 && (
                <FormControl fullWidth size="small">
                  <InputLabel id="officer-picker-label" sx={{ fontWeight: 600 }}>Select Officer Account</InputLabel>
                  <Select
                    labelId="officer-picker-label"
                    label="Select Officer Account"
                    value={selectedOfficerId || matchingOfficers[0]._id || matchingOfficers[0].id}
                    onChange={(e) => setSelectedOfficerId(e.target.value)}
                    sx={{ backgroundColor: '#FFFFFF', borderRadius: 2 }}
                  >
                    {matchingOfficers.map((o) => {
                      const name = o.firstName ? `${o.firstName} ${o.lastName || ''}`.trim() : o.username;
                      const count = o.assignedCount !== undefined ? o.assignedCount : 0;
                      const isRestricted = pendingRole === 'financial_2' || pendingRole === 'financial_3';
                      const idInfo = o.membershipId ? `ID: ${o.membershipId}` : (o.registrationNo ? `Reg: ${o.registrationNo}` : '');
                      return (
                        <MenuItem key={o._id || o.id} value={o._id || o.id}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', alignItems: 'center' }}>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: 700, color: '#0F172A' }}>{name}</Typography>
                              {idInfo && <Typography variant="caption" sx={{ color: '#64748B' }}>{idInfo}</Typography>}
                            </Box>
                            {isRestricted && (
                              <Chip
                                size="small"
                                label={`${count} assigned`}
                                sx={{ height: 20, fontSize: '0.68rem', fontWeight: 700, bgcolor: '#ECFDF5', color: '#047857' }}
                              />
                            )}
                          </Box>
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              )}

              {/* Officer Comprehensive Information Card */}
              {selectedOfficer && (
                <Box
                  sx={{
                    backgroundColor: '#FFFFFF',
                    borderRadius: 2.5,
                    border: '1px solid #E2E8F0',
                    p: { xs: 2, sm: 2.5 },
                    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                  }}
                >
                  {/* Officer Top Header */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.8, mb: 2, pb: 2, borderBottom: '1px solid #F1F5F9' }}>
                    <Avatar
                      sx={{
                        width: 48,
                        height: 48,
                        bgcolor: '#0B3B60',
                        color: '#FFFFFF',
                        fontWeight: 700,
                        fontSize: '1.2rem',
                      }}
                    >
                      {(selectedOfficer.firstName || selectedOfficer.username || 'O').charAt(0).toUpperCase()}
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#0F172A', lineHeight: 1.2 }}>
                          {selectedOfficer.firstName
                            ? `${selectedOfficer.firstName} ${selectedOfficer.middleName ? selectedOfficer.middleName + ' ' : ''}${selectedOfficer.lastName || ''}`.trim()
                            : selectedOfficer.username}
                        </Typography>
                        <Chip
                          size="small"
                          label={getStatusChipProps(selectedOfficer.approvedOrNotApproved).label}
                          sx={{
                            height: 20,
                            fontSize: '0.68rem',
                            fontWeight: 700,
                            bgcolor: getStatusChipProps(selectedOfficer.approvedOrNotApproved).bgcolor,
                            color: getStatusChipProps(selectedOfficer.approvedOrNotApproved).color,
                          }}
                        />
                        {selectedOfficer.sex && (
                          <Chip
                            size="small"
                            label={selectedOfficer.sex.toUpperCase()}
                            sx={{ height: 20, fontSize: '0.68rem', fontWeight: 600, bgcolor: '#F1F5F9', color: '#475569' }}
                          />
                        )}
                      </Box>
                      <Typography variant="caption" sx={{ color: '#64748B', display: 'block', mt: 0.3 }}>
                        @{selectedOfficer.username} {selectedOfficer.email ? `• ${selectedOfficer.email}` : ''}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Officer Structured Fields Grid */}
                  <Grid container spacing={1.5}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600, display: 'block' }}>Member ID / Reg No:</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#0F172A' }}>
                        {selectedOfficer.membershipId ? `ID: ${selectedOfficer.membershipId}` : (selectedOfficer.registrationNo ? `Reg: ${selectedOfficer.registrationNo}` : 'N/A')}
                      </Typography>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600, display: 'block' }}>Phone Number:</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 700, color: '#0F172A' }}>
                        {selectedOfficer.phoneNumber || 'N/A'}
                      </Typography>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600, display: 'block' }}>Marital Status:</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: '#0F172A' }}>
                        {selectedOfficer.maritalStatus || 'N/A'}
                      </Typography>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600, display: 'block' }}>System Roles:</Typography>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.3 }}>
                        {(selectedOfficer.roles || []).map((r, i) => (
                          <Chip
                            key={i}
                            size="small"
                            label={typeof r === 'object' ? r.name : String(r)}
                            sx={{ height: 20, fontSize: '0.68rem', fontWeight: 700, bgcolor: '#EFF6FF', color: '#1D4ED8' }}
                          />
                        ))}
                      </Box>
                    </Grid>

                    {/* Allocated Members Portfolio Callout for Financial Roles */}
                    {(pendingRole === 'financial' || pendingRole === 'financial_2' || pendingRole === 'financial_3') && (
                      <Grid item xs={12}>
                        <Box sx={{ mt: 0.5, p: 1.5, backgroundColor: 'rgba(5, 150, 105, 0.08)', borderRadius: 2, border: '1px solid rgba(5, 150, 105, 0.25)' }}>
                          <Typography variant="caption" sx={{ color: '#047857', fontWeight: 800, textTransform: 'uppercase', display: 'block', letterSpacing: '0.04em' }}>
                            Allocated Member Portfolio Scope
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#065F46', fontWeight: 700, mt: 0.3 }}>
                            {selectedOfficer.assignedCount !== undefined ? selectedOfficer.assignedCount : 0} Members Assigned Under Management
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#047857', display: 'block', fontSize: '0.72rem', mt: 0.2 }}>
                            {pendingRole === 'financial_2' || pendingRole === 'financial_3'
                              ? 'This officer can view and supervise only the transactions belonging to these assigned members.'
                              : 'This officer has primary executive oversight and allocation control.'}
                          </Typography>
                        </Box>
                      </Grid>
                    )}

                    {/* Personal Balances Overview */}
                    <Grid item xs={12}>
                      <Box sx={{ mt: 0.5, p: 1.5, backgroundColor: '#F8FAFC', borderRadius: 2, border: '1px solid #E2E8F0' }}>
                        <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 800, textTransform: 'uppercase', display: 'block', letterSpacing: '0.04em', mb: 0.5 }}>
                          Officer Personal Account Ledger
                        </Typography>
                        <Grid container spacing={1}>
                          <Grid item xs={4}>
                            <Typography variant="caption" sx={{ color: '#64748B', display: 'block' }}>Shares</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#0B3B60' }}>₦{fNumber(selectedOfficer.sharesBalance || 0)}</Typography>
                          </Grid>
                          <Grid item xs={4}>
                            <Typography variant="caption" sx={{ color: '#64748B', display: 'block' }}>Savings</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#059669' }}>₦{fNumber(selectedOfficer.savingsBalance || 0)}</Typography>
                          </Grid>
                          <Grid item xs={4}>
                            <Typography variant="caption" sx={{ color: '#64748B', display: 'block' }}>Loan</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#D97706' }}>₦{fNumber(selectedOfficer.loanBalance || 0)}</Typography>
                          </Grid>
                        </Grid>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              )}
            </Stack>
          )}
        </DialogContent>

        <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: 2, backgroundColor: '#FFFFFF', borderTop: '1px solid #E2E8F0', justifyContent: 'space-between' }}>
          <Button
            onClick={() => setOfficerModalOpen(false)}
            variant="outlined"
            sx={{
              color: '#64748B',
              borderColor: '#CBD5E1',
              borderRadius: 2,
              fontWeight: 600,
              textTransform: 'none',
              '&:hover': { borderColor: '#94A3B8', backgroundColor: '#F8FAFC' },
            }}
          >
            Cancel
          </Button>

          <Button
            onClick={handleConfirmOfficer}
            variant="contained"
            sx={{
              backgroundColor: '#0B3B60',
              color: '#FFFFFF',
              borderRadius: 2,
              fontWeight: 700,
              textTransform: 'none',
              px: 3,
              boxShadow: '0 4px 12px rgba(11, 59, 96, 0.25)',
              '&:hover': { backgroundColor: '#072740' },
            }}
          >
            {selectedOfficer
              ? `Preview As ${selectedOfficer.firstName || selectedOfficer.username}`
              : `Test ${roleLabels[pendingRole] || 'Role'} Simulation`}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
